import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { storeReplacementThreshold } from "../../../Store/TemplateStore/template.action";
import {
  REPLACEMENT_THRESHOLD,
  FIELD_RELATIONSHIPS,
} from "./replacementThreshold.config";
import { WPRangedInput } from "../../../packages/WPInput";
import IconWithTooltip from "../../../common/styles/components/headings/IconWithTooltip";
import { COMPARE_KEYS } from "../../../constants/global.constants";

import "./index.scss";

const ReplacementThreshold = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    max_feed_pressure,
    max_permeate_quality,
    std_warranty_period,
    startup_pressure_drop,
    designed_permeate_flow,
    std_membrane_age,
    max_pressure_drop,
    min_permeate_flow,
    flow,
    pressure,
    permeate_quality,
  } = useSelector((state) => state);

  const thresholdFieldsConfig = useMemo(
    () => REPLACEMENT_THRESHOLD({ pressure, permeate_quality, flow }),
    [pressure, permeate_quality, flow],
  );

  const [thresholdState, setThresholdState] = useState({
    max_feed_pressure,
    max_permeate_quality,
    std_warranty_period,
    startup_pressure_drop,
    designed_permeate_flow,
    std_membrane_age,
    max_pressure_drop,
    min_permeate_flow,
  });

  useEffect(() => {
    setThresholdState({
      max_feed_pressure,
      max_permeate_quality,
      std_warranty_period,
      startup_pressure_drop,
      designed_permeate_flow,
      std_membrane_age,
      max_pressure_drop,
      min_permeate_flow,
    });
  }, [
    max_feed_pressure,
    max_permeate_quality,
    std_warranty_period,
    startup_pressure_drop,
    designed_permeate_flow,
    std_membrane_age,
    max_pressure_drop,
    min_permeate_flow,
  ]);

  const [errors, setErrors] = useState({
    max_feed_pressure: null,
    max_permeate_quality: null,
    std_warranty_period: null,
    startup_pressure_drop: null,
    designed_permeate_flow: null,
    std_membrane_age: null,
    max_pressure_drop: null,
    min_permeate_flow: null,
  });

  const [noError, setNoError] = useState(true);

  useEffect(() => {
    const noError = Object.values(errors).every(
      (error) => error === "" || error === null,
    );
    setNoError(noError);
    props.setNoThresholdError(noError);
  }, [errors]);

  useEffect(() => {
    let allMandatoryFieldsFilled = true;
    Object.keys(thresholdFieldsConfig).forEach((fieldId) => {
      const fieldConfig = thresholdFieldsConfig[fieldId];
      const { isRequired } = fieldConfig;
      if (isRequired) {
        const hasValue = thresholdState[fieldId];
        if (hasValue === null || hasValue === undefined || hasValue === "") {
          setErrors({
            ...errors,
            [fieldId]: true,
          });
          allMandatoryFieldsFilled = false;
        }
      }
    });
    props.setMandatoryThresholdFilled(allMandatoryFieldsFilled);
  }, [thresholdState, props.isNextClicked]);

  const formatRelatedFields = (field, value) => {
    const relationship = FIELD_RELATIONSHIPS[field];

    if (!relationship) return null;

    const { targetField, conversion } = relationship;
    const calculatedValue = value === "" ? "" : conversion(value);

    return { [targetField]: calculatedValue };
  };

  const validateCrossFieldRules = (fieldName, numValue) => {
    // Check if current field is a target field
    const sourceField = Object.keys(FIELD_RELATIONSHIPS).find(
      (key) => FIELD_RELATIONSHIPS[key].targetField === fieldName,
    );

    if (sourceField) {
      const sourceValue = thresholdState[sourceField];
      const { compareKey } = FIELD_RELATIONSHIPS[sourceField];

      if (
        sourceValue !== null &&
        sourceValue !== undefined &&
        sourceValue > 0
      ) {
        if (compareKey === COMPARE_KEYS.GREATER && numValue <= sourceValue) {
          return {
            compareKey: COMPARE_KEYS.GREATER,
            fieldName,
            sourceField,
          };
        } else if (
          compareKey === COMPARE_KEYS.LESS &&
          numValue >= sourceValue
        ) {
          return {
            compareKey: COMPARE_KEYS.LESS,
            fieldName,
            sourceField,
          };
        }
      }
    }
    return null;
  };

  const getRelativeCompareErrorMessage = (fieldId) => {
    const error = errors[fieldId];
    if (error?.compareKey) {
      const { fieldName, sourceField, compareKey } = error;
      const compareText =
        compareKey === COMPARE_KEYS.GREATER
          ? "ValueStrings.mustBeGreaterThan"
          : "ValueStrings.mustBeLessThan";
      return `${t(`replacementThresholdStrings.${fieldName}`)} ${t(
        compareText,
      )} ${t(`replacementThresholdStrings.${sourceField}`)}`;
    }
    return null;
  };

  const handleInputChange = (field, event, ranges) => {
    const value = event.target.value;
    const { min, max } = ranges.hard;
    const numValue = value === "" ? "" : parseFloat(value);

    const newThresholdState = { ...thresholdState, [field]: numValue };
    const newFormattedFields = formatRelatedFields(field, value);
    let updatedErrors = { ...errors };

    if (Object.keys(newFormattedFields || {}).length > 0) {
      Object.assign(newThresholdState, newFormattedFields);
      const targetField = Object.keys(newFormattedFields)[0];
      updatedErrors = { ...updatedErrors, [targetField]: null };
    }

    const isEmpty = value === "" || value === null || value === undefined;
    const isInvalid = isEmpty || numValue < min || numValue > max;
    const crossFieldErrorMsg = !isEmpty
      ? validateCrossFieldRules(field, numValue)
      : null;

    if (isInvalid) {
      updatedErrors[field] = true;
    } else if (crossFieldErrorMsg) {
      updatedErrors[field] = crossFieldErrorMsg;
    } else {
      updatedErrors[field] = null;
    }
    setErrors(updatedErrors);
    setThresholdState(newThresholdState);
    dispatch(storeReplacementThreshold(newThresholdState));
  };

  const replacementThreshold = () => {
    const permeateFlow = thresholdState.designed_permeate_flow;
    if (noError && permeateFlow) {
      const thresholdValues = {
        max_feed_pressure: thresholdState.max_feed_pressure,
        max_permeate_quality: thresholdState.max_permeate_quality,
        std_warranty_period: thresholdState.std_warranty_period,
        startup_pressure_drop: thresholdState.startup_pressure_drop,
        designed_permeate_flow: thresholdState.designed_permeate_flow,
        std_membrane_age: thresholdState.std_membrane_age,
        max_pressure_drop: thresholdState.max_pressure_drop,
        min_permeate_flow: thresholdState.min_permeate_flow,
      };
      dispatch(storeReplacementThreshold(thresholdValues));
    } else {
      props.setActiveStep(0);
    }
  };
  React.useImperativeHandle(ref, () => ({ replacementThreshold }));

  return (
    <div className="ro-card-wrapper position-relative">
      <div className="ro-card-header">
        <div className="info-section">
          <div className="teal-heading">
            {t("replacementThresholdStrings.replacementThresholds")}
          </div>
          <IconWithTooltip
            label={t("helpInformation.replacementThresholdHelp")}
          />
        </div>
      </div>
      <div className="grid-container-threshold">
        {Object.keys(thresholdFieldsConfig).map((fieldId, index) => {
          const fieldConfig = thresholdFieldsConfig[fieldId];
          const { unitLabel, ranges, isRequired } = fieldConfig;
          return (
            <div className="grid-item-threshold " key={index}>
              <WPRangedInput
                name={fieldId}
                key={fieldId}
                label={t(`replacementThresholdStrings.${fieldId}`)}
                value={thresholdState[fieldId] || ""}
                onChange={(event) => handleInputChange(fieldId, event, ranges)}
                suffix={unitLabel}
                ranges={ranges}
                required={isRequired}
                requiredMsg={
                  isRequired ? t("ValueStrings.requiredField") : null
                }
                externalError={getRelativeCompareErrorMessage(fieldId)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default ReplacementThreshold;
